/* eslint-disable no-undef */
/**
 * Waits for the document body to reach a stable height by monitoring resize events.
 * This utility function is useful for ensuring that dynamic content has finished loading
 * and the page layout has stabilized before proceeding with other operations. It uses
 * a ResizeObserver to monitor body height changes and resolves when the height remains
 * unchanged for a specified quiet period or when a maximum wait time is reached.
 *
 * @returns A Promise that resolves when the body height has been stable for the quiet period or the maximum wait time is reached.
 *
 * @example
 * ```tsx
 * // Wait for page layout to stabilize before taking a screenshot
 * await stableBody();
 * console.log('Body height is now stable');
 *
 * // Use in component lifecycle
 * useEffect(() => {
 *   const waitForStableLayout = async () => {
 *     await stableBody();
 *     // Perform operations that require stable layout
 *   };
 *   waitForStableLayout();
 * }, []);
 * ```
 */
export const stableBody = async () => {
    const quietMs = 50;
    const maxWaitMs = 3_000;

    return new Promise<void>(resolve => {
        // eslint-disable-next-line react-hooks-ssr/react-hooks-global-ssr
        let lastHeight = document.body.clientHeight;
        let stableTimer: NodeJS.Timeout | null = null;
        let maxTimer: NodeJS.Timeout | null = null;
        let initialized = false;
        let done = false;

        /**
         * Cleanup function that resolves the promise and performs necessary cleanup operations.
         * This function ensures that the ResizeObserver is properly disconnected, all timers are cleared,
         * and the promise is resolved only once to prevent memory leaks and multiple resolutions.
         * It implements a safety mechanism using the `done` flag to prevent duplicate cleanup calls.
         *
         * @example
         * ```tsx
         * // Called internally when body height stabilizes
         * const finish = () => {
         *   if (done) return;
         *   done = true;
         *   // Cleanup and resolve
         * };
         * ```
         */
        const finish = () => {
            if (done) return;
            done = true;
            try {
                BodyResizeObserver.disconnect();
            } catch {}
            if (stableTimer) clearTimeout(stableTimer);
            if (maxTimer) clearTimeout(maxTimer);
            resolve();
        };

        const BodyResizeObserver = new ResizeObserver(entries => {
            let bodyEntry = null;

            for (const entry of entries) {
                if (entry.target === document.body) {
                    bodyEntry = entry;
                    break;
                }
            }

            if (!bodyEntry) return;

            const {height} = bodyEntry.contentRect;

            if (!initialized) {
                initialized = true;
                lastHeight = height;
                stableTimer = setTimeout(finish, quietMs);

                return;
            }

            if (height !== lastHeight) {
                lastHeight = height;
                if (stableTimer) clearTimeout(stableTimer);
                stableTimer = setTimeout(finish, quietMs);
            }
        });

        // eslint-disable-next-line react-hooks-ssr/react-hooks-global-ssr
        BodyResizeObserver.observe(document.body);

        maxTimer = setTimeout(finish, maxWaitMs);
    });
};